import React, { Children, ReactNode } from 'react';
import { calculateHeight, initHeight } from './ContentList.utils';
import { useIsomorphicLayoutEffect } from '../../../hooks/useIsomorphicLayoutEffect';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import bem from '../../../lib/bemUtils/bem';

export enum ContentListModifier {
  BACKGROUND_TRANSPARENT = 'background-transparent',
}

type ContentListProperties = {
  id?: string;
  children?: ReactNode;
  ordered?: boolean;
  maxVisibleItems?: number;
  modifier?: Modifier<ContentListModifier>;
};

/**
 * A component wrapping all children in a (un-) ordered list with the
 * possibility of showing only N children.
 *
 * This is accomplished by accumulating all references to child elements during
 * render and calculating/setting the desired height before react commits
 * changes to the DOM (using `useLayoutEffect`).
 */
export function ContentList({ id, children, maxVisibleItems, ordered = false, modifier }: ContentListProperties) {
  const [height, setHeight] = React.useState<number | undefined>(undefined);
  const childRefs: (HTMLLIElement | null)[] = [];

  // calculate the height prior rendering to prevent 2nd render
  useIsomorphicLayoutEffect(() => {
    if (
      (maxVisibleItems && maxVisibleItems <= Children.count(children)) ||
      initHeight(children, height, maxVisibleItems)
    ) {
      const newHeight = calculateHeight(childRefs, maxVisibleItems);
      setHeight(newHeight);
    }
  }, [maxVisibleItems, children, height, childRefs]);

  const List = ordered ? 'ol' : 'ul';

  return children && React.Children.count(children) > 0 ? (
    <List
      className={bem('content-list', modifier)}
      id={id}
      style={height ? { height, overflow: 'auto' } : undefined}
      tabIndex={maxVisibleItems && height ? 0 : undefined}
    >
      {children}
    </List>
  ) : null;
}
